/**
 * 라우터 설정
 * - 라우트 정의 및 구성
 * - 인증 가드 설정
 * - 레이아웃 구성
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

// 페이지 컴포넌트들 - 모듈별 import
import { LoginPage } from '../pages/auth';
import { DashboardPage } from '../pages/dashboard';
import { MemberListPage, MemberDetail } from '../pages/members';
import { BusinessListPage, BusinessDetail } from '../pages/business';
import {
  CreditPaymentPage,
  OnlinePaymentPage,
  OfflinePaymentPage,
} from '../pages/payment';
import {
  SettlementDetailPage,
  RevenueSettlementPage,
} from '../pages/settlement';
// 알림장 — 메뉴 비노출 (요구사항 재정의 전까지 보류). 재등록 시 import 재활성화
// import {
//   NotificationListPage,
//   NotificationSendPage,
//   NotificationDetailPage
// } from '../pages/notifications';
import {
  AnnouncementListPage,
  AnnouncementCreatePage,
  AnnouncementDetailPage,
} from '../pages/announcements';
import { BannerListPage, BannerCreatePage } from '../pages/banners';
import { InquiryListPage, InquiryDetailPage } from '../pages/inquiries';
import {
  TermsListPage,
  TermsEditorPage,
  TermsDetailPage,
} from '../pages/terms';
import { DailyReservationsPage } from '../pages/daily-reservations';
import { TicketListPage } from '../pages/tickets';
import {
  PushMessageListPage,
  PushMessageCreatePage,
} from '../pages/push-messages';
import { AppVersionPolicyPage } from '../pages/app-version';
// API 테스트 — 개발용 도구, 메뉴 비노출. 재등록 시 import 재활성화
// import { ApiTestPage } from '../pages/api';
import { AuditLogListPage } from '../pages/audit-logs';
import { BusinessActivityLogPage } from '../pages/business-logs';
import { GuardianActivityLogPage } from '../pages/guardian-logs';
import { BusinessMenuPage } from '../pages/business-menu';
import {
  CreditDashboardPage,
  CreditPricesPage,
  // CreditPoliciesPage,  // 1차 출시 미도입 — 추후 사이드바에 다시 등록 시 import 재활성화
  CreditPackagesPage,
  // CreditCompaniesPage 제거 — 사업자 크레딧은 매장 목록 '크레딧' 탭으로 통합
  // CreditBulkGrantPage,  // 1차 출시 미도입 — 사업장별 화면에서 개별 지급으로 대응
  CreditChangesPage,
} from '../pages/credit';
import { ConditionsHubPage, CreditConditionsPage } from '../pages/conditions';
import { KakaoAlimTemplatesPage } from '../pages/kakao-alim/KakaoAlimTemplatesPage';
import { KakaoAlimLogsPage } from '../pages/kakao-alim/KakaoAlimLogsPage';

// 레이아웃 컴포넌트
import { MainLayout } from '../components/Layout/MainLayout';

/**
 * 애플리케이션 라우터
 * - 모든 라우트 정의
 * - 레이아웃 구성
 * - 리다이렉트 처리
 */
export function AppRouter() {
  return (
    <Routes>
      {/* 로그인 페이지 - 레이아웃 없음 */}
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />

      {/* 메인 레이아웃이 적용된 페이지들 */}
      <Route path={ROUTES.HOME} element={<MainLayout />}>
        {/* 기본 리다이렉트 */}
        <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />

        {/* 대시보드 */}
        <Route path="dashboard" element={<DashboardPage />} />

        {/* 회원 관리 */}
        <Route path="members" element={<MemberListPage />} />
        <Route path="members/:userSeq" element={<MemberDetail />} />

        {/* 매장 관리 */}
        <Route path="business" element={<BusinessListPage />} />
        <Route path="business/:companySeq" element={<BusinessDetail />} />

        {/* 결제 및 정산 정보 — 하위 메뉴 (크레딧/온라인/오프라인) */}
        {/* 매출은 매출/정산 통합 페이지로 이전 → 기존 북마크 보호용 리다이렉트 */}
        <Route
          path="revenue"
          element={<Navigate to="/settlement?tab=revenue" replace />}
        />
        <Route path="payment" element={<Navigate to="/payment/credit" replace />} />
        <Route path="payment/credit" element={<CreditPaymentPage />} />
        <Route path="payment/online" element={<OnlinePaymentPage />} />
        <Route path="payment/offline" element={<OfflinePaymentPage />} />

        {/* 매출/정산 통합 (탭: 매출 | 정산) */}
        <Route path="settlement" element={<RevenueSettlementPage />} />
        <Route
          path="settlement/:id/:yearMonth"
          element={<SettlementDetailPage />}
        />

        {/* 알림 관리 */}
        {/* 알림장 — 메뉴 비노출 (요구사항 재정의 전까지 보류)
        <Route path="notifications" element={<NotificationListPage />} />
        <Route path="notifications/send" element={<NotificationSendPage />} />
        <Route path="notifications/:id" element={<NotificationDetailPage />} />
        */}

        {/* 공지사항 (시스템 업데이트 공지) */}
        <Route path="announcements" element={<AnnouncementListPage />} />
        <Route path="announcements/new" element={<AnnouncementCreatePage />} />
        <Route
          path="announcements/:id/edit"
          element={<AnnouncementCreatePage mode="edit" />}
        />
        <Route path="announcements/:id" element={<AnnouncementDetailPage />} />

        {/* 홈 배너 */}
        <Route path="banners" element={<BannerListPage />} />
        <Route path="banners/new" element={<BannerCreatePage />} />
        <Route path="banners/:id/edit" element={<BannerCreatePage mode="edit" />} />

        {/* 문의사항 (정보 수정 및 제안 / 개선사항) */}
        <Route path="inquiries" element={<InquiryListPage />} />
        <Route path="inquiries/:id" element={<InquiryDetailPage />} />

        {/* 약관 관리 — 개정은 새 버전 등록, 시행된 버전은 읽기 전용 */}
        <Route path="terms" element={<TermsListPage />} />
        <Route path="terms/new" element={<TermsEditorPage />} />
        <Route path="terms/:id/edit" element={<TermsEditorPage mode="edit" />} />
        <Route path="terms/:id" element={<TermsDetailPage />} />

        {/* 일자별 예약 */}
        <Route path="daily-reservations" element={<DailyReservationsPage />} />

        {/* 이용권 관리 */}
        <Route path="tickets" element={<TicketListPage />} />

        {/* Push 발송 */}
        <Route path="push-messages" element={<PushMessageListPage />} />
        <Route path="push-messages/new" element={<PushMessageCreatePage />} />

        {/* 앱 버전 정책 */}
        <Route path="app-version-policies" element={<AppVersionPolicyPage />} />

        {/* 사업자 메뉴 관리 */}
        <Route path="business-menu" element={<BusinessMenuPage />} />

        {/* 크레딧 관리 — 단가·정책·패키지·변경이력 */}
        <Route path="credit" element={<Navigate to={ROUTES.CREDIT_DASHBOARD} replace />} />
        <Route path="credit/dashboard" element={<CreditDashboardPage />} />
        <Route path="credit/prices" element={<CreditPricesPage />} />
        {/* 지급 정책(/credit/policies) 은 1차 출시 미도입 — 사이드바·라우트 모두 비활성 */}
        <Route path="credit/packages" element={<CreditPackagesPage />} />
        {/* 사업자 크레딧(/credit/companies) 은 매장 목록 '크레딧' 탭으로 통합 → 라우트 제거 */}
        {/* 일괄 지급(/credit/bulk-grant) 은 1차 출시 미도입 */}
        <Route path="credit/changes" element={<CreditChangesPage />} />

        {/* 활동 로그 (도메인 이벤트) */}
        <Route path="business-logs" element={<BusinessActivityLogPage />} />
        <Route path="guardian-logs" element={<GuardianActivityLogPage />} />

        {/* API 감사 로그 */}
        <Route path="audit-logs" element={<AuditLogListPage />} />

        {/* 조건 설정 — 최상위 (운영 정책 통합) */}
        <Route path="conditions" element={<ConditionsHubPage />} />
        <Route path="conditions/credit" element={<CreditConditionsPage />} />

        {/* 알림톡 템플릿 관리 */}
        <Route path="kakao-alim/templates" element={<KakaoAlimTemplatesPage />} />
        <Route path="kakao-alim/logs" element={<KakaoAlimLogsPage />} />

        {/* API 테스트 — 개발용 도구, 메뉴 비노출
        <Route path="api-test" element={<ApiTestPage />} />
        */}
      </Route>

      {/* 404 처리 - 대시보드로 리다이렉트 */}
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
}
